import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../client';
import type {
    LedgerReturn,
    LedgerReturnType,
    PaginatedResponse,
    CreateLedgerReturnData,
    UpdateLedgerReturnData,
} from '../types';

const PAGE_SIZE = 20;

/**
 * Options for the useLedgerReturns hook.
 */
export interface UseLedgerReturnsOptions {
    /** Optional filter by return type (inflow/outflow). */
    type?: LedgerReturnType | null;
}

/**
 * Hook for fetching ledger returns with infinite scroll pagination.
 * Supports optional type filter for inflows/outflows.
 */
export function useLedgerReturns(ledgerId: number, options: UseLedgerReturnsOptions = {}) {
    const { type } = options;

    return useInfiniteQuery({
        queryKey: ['ledgers', ledgerId, 'returns', { type }],
        queryFn: async ({ pageParam = 0 }) => {
            const params = new URLSearchParams();
            params.append('limit', String(PAGE_SIZE));
            params.append('offset', String(pageParam));
            params.append('order', 'DESC');

            if (type) {
                params.append('type', type);
            }

            return api.get<PaginatedResponse<LedgerReturn>>(
                `/api/ledgers/${ledgerId}/returns?${params.toString()}`
            );
        },
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_SIZE;
            // If we've fetched all items, return undefined to signal no more pages.
            if (nextOffset >= lastPage.total) {
                return undefined;
            }
            return nextOffset;
        },
        enabled: !!ledgerId,
    });
}

/**
 * Hook for creating a new ledger return.
 * Invalidates the returns list cache on success.
 */
export function useCreateLedgerReturn(ledgerId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: CreateLedgerReturnData) =>
            api.post<LedgerReturn>(`/api/ledgers/${ledgerId}/returns`, data),
        onSuccess: () => {
            // Invalidate returns list to refetch with new data.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'returns'] });
            // Also invalidate the ledger itself as analytics may have changed.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId] });
            // And the ledgers list for updated analytics.
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ALL ledger-detail-batch queries so other ledgers see updated userMaxValueDate
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
        onError: (error, variables) => {
            console.error('[useLedgerReturns] Create return failed:', {
                ledgerId,
                data: variables,
                error: error instanceof ApiError ? { status: error.status, data: error.data } : error,
            });
        },
    });
}

/**
 * Hook for updating an existing ledger return.
 * Invalidates the returns list cache on success.
 */
export function useUpdateLedgerReturn(ledgerId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ returnId, data }: { returnId: number; data: UpdateLedgerReturnData }) =>
            api.put<LedgerReturn>(`/api/ledgers/${ledgerId}/returns/${returnId}`, data),
        onSuccess: () => {
            // Invalidate returns list to refetch with updated data.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'returns'] });
            // Also invalidate the ledger itself as analytics may have changed.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId] });
            // And the ledgers list for updated analytics.
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ALL ledger-detail-batch queries so other ledgers see updated userMaxValueDate
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
        onError: (error, variables) => {
            console.error('[useLedgerReturns] Update return failed:', {
                ledgerId,
                returnId: variables.returnId,
                data: variables.data,
                error: error instanceof ApiError ? { status: error.status, data: error.data } : error,
            });
        },
    });
}

/**
 * Hook for deleting a ledger return.
 * Invalidates the returns list cache on success.
 */
export function useDeleteLedgerReturn(ledgerId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (returnId: number) => {
            await api.delete<void>(`/api/ledgers/${ledgerId}/returns/${returnId}`);
        },
        onSuccess: () => {
            // Invalidate returns list to refetch after deletion.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId, 'returns'] });
            // Also invalidate the ledger itself as analytics may have changed.
            queryClient.invalidateQueries({ queryKey: ['ledgers', ledgerId] });
            // And the ledgers list for updated analytics.
            queryClient.invalidateQueries({ queryKey: ['ledgers'] });
            queryClient.invalidateQueries({ queryKey: ['batch-ledgers'] });
            // Invalidate ALL ledger-detail-batch queries so other ledgers see updated userMaxValueDate
            queryClient.invalidateQueries({ queryKey: ['ledger-detail-batch'] });
            // Invalidate analytics queries to refetch dashboard and charts
            queryClient.invalidateQueries({ queryKey: ['ledgers', 'analytics'] });
            queryClient.invalidateQueries({ queryKey: ['batch-dashboard-analytics'] });
        },
        onError: (error, returnId) => {
            console.error('[useLedgerReturns] Delete return failed:', {
                ledgerId,
                returnId,
                error: error instanceof ApiError ? { status: error.status, data: error.data } : error,
            });
        },
    });
}
